import React, { useMemo, useRef, useState } from 'react';
import { Container, Grid, Card, Typography, Button, Modal, Box, Chip, Tabs, Tab } from '@material-ui/core';
import useDigitalMediaStyles from './assets/css/_digitalMedia';
import { digitalMediaServices } from './components/theme/dataset';
import AnimatedSection from './components/AnimatedSection';
import ContactForm from './components/Forms/ContactForm';



const processSteps = [
  {
    step: '01',
    title: 'Discovery',
    text: 'We align on goals, audience, messaging, and the creative outcome before production starts.',
  },
  {
    step: '02',
    title: 'Creative Direction',
    text: 'We shape the visual system, content structure, and delivery path into one cohesive plan.',
  },
  {
    step: '03',
    title: 'Production',
    text: 'We create polished assets with the right balance of speed, quality, and brand consistency.',
  },
  {
    step: '04',
    title: 'Launch & Optimize',
    text: 'We deliver, refine, and scale the work so it continues to support the business objective.',
  },
];

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`digital-media-tabpanel-${index}`}
      aria-labelledby={`digital-media-tab-${index}`}
      {...other}
    >
      {value === index && <div style={{ paddingTop: 32 }}>{children}</div>}
    </div>
  );
}

const getUniqueToolCount = (services) => {
  const tools = new Set();

  services.forEach((service) => {
    (service.tools || []).forEach((tool) => tools.add(tool));
  });

  return tools.size;
};

export default function DigitalMediaServicesPage() {
  const classes = useDigitalMediaStyles();
  const [tabValue, setTabValue] = useState(0);
  const [selectedService, setSelectedService] = useState(null);
  const [open, setOpen] = useState(false);
  const [prefilledService, setPrefilledService] = useState(null);
  const contactRef = useRef(null);

  const totalServices = useMemo(
    () => digitalMediaServices.reduce((count, category) => count + category.services.length, 0),
    []
  );

  const totalDeliverables = useMemo(
    () => digitalMediaServices.reduce(
      (count, category) => count + category.services.reduce((serviceCount, service) => serviceCount + (service.deliverables ? service.deliverables.length : 0), 0),
      0
    ),
    []
  );

  const totalTools = useMemo(
    () => digitalMediaServices.reduce((count, category) => count + getUniqueToolCount(category.services), 0),
    []
  );

  const heroStats = [
    { label: 'Service categories', value: digitalMediaServices.length, note: 'Creative disciplines' },
    { label: 'Service modules', value: totalServices, note: 'Ready to scope and deploy' },
    { label: 'Deliverables mapped', value: totalDeliverables, note: 'Execution-ready outputs' },
    { label: 'Tools covered', value: totalTools, note: 'Production stack coverage' },
  ];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenModal = (service, category) => {
    setSelectedService({ ...service, category });
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setSelectedService(null);
  };

  const scrollToContact = () => {
    if (contactRef.current) {
      contactRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToCategories = () => {
    const categoriesSection = document.getElementById('service-categories');

    if (categoriesSection) {
      categoriesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleRequestQuote = (serviceName = null) => {
    if (!serviceName || !selectedService) {
      setPrefilledService(serviceName || selectedService?.title || 'Digital Media Services');
    } else {
      setPrefilledService(serviceName);
    }

    handleCloseModal();
    window.setTimeout(scrollToContact, 150);
  };

  const handleCategoryQuote = (categoryName) => {
    setPrefilledService(categoryName);
    window.setTimeout(scrollToContact, 0);
  };

  return (
    <>
      <div className={classes.root} id="digital-media-services">
        <Container maxWidth="lg" className={classes.pageContainer}>
          <section className={classes.heroSection}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <AnimatedSection animation="fadeInUp" delay={0}>
                  <Chip label="Digital Media Services" className={classes.heroEyebrow} />
                  <Typography component="h1" variant="h2" className={classes.heroTitle}>
                    Premium digital media designed to move brands forward.
                  </Typography>
                  <Typography component="p" className={classes.heroDescription}>
                    Comprehensive creative solutions to elevate your brand presence, sharpen your message, and engage your audience across every digital platform.
                  </Typography>
                  <div className={classes.heroActions} style={{ gap: '12px' }}>
                    <Button
                      variant="contained"
                      className={classes.primaryHeroButton}
                      onClick={() => handleCategoryQuote('Digital Media Services')}
                    >
                      Request a Quote
                    </Button>
                    <Button
                      variant="outlined"
                      className={classes.secondaryHeroButton}
                      onClick={scrollToCategories}
                    >
                      Browse Services
                    </Button>
                  </div>
                </AnimatedSection>
              </Grid>

              <Grid item xs={12} md={5}>
                <AnimatedSection animation="fadeInRight" delay={120}>
                  <Card className={classes.heroPanel} elevation={0}>
                    <Chip label="Conversion-focused studio" className={classes.heroPanelChip} />
                    <Typography variant="h4" component="h2" className={classes.heroPanelTitle}>
                      Creative work that feels structured, trustworthy, and premium.
                    </Typography>
                    <Typography className={classes.heroPanelText}>
                      We combine planning, production, and performance thinking so every deliverable supports the bigger business goal.
                    </Typography>
                    <div className={classes.heroSupportList}>
                      {[
                        'Brand-first creative direction',
                        'Production, launch, and optimization support',
                        'Clear timelines with polished deliverables',
                      ].map((item) => (
                        <div className={classes.heroSupportItem} key={item}>
                          <span className={classes.heroSupportDot} />
                          <Typography component="span" className={classes.heroSupportText}>
                            {item}
                          </Typography>
                        </div>
                      ))}
                    </div>
                  </Card>
                </AnimatedSection>
              </Grid>
            </Grid>

            <Grid container spacing={3} className={classes.statsGrid}>
              {heroStats.map((stat) => (
                <Grid item xs={12} sm={6} md={3} key={stat.label}>
                  <AnimatedSection animation="fadeInUp" delay={80}>
                    <Card className={classes.statCard} elevation={0}>
                      <Typography className={classes.statValue} component="div">
                        {stat.value}
                      </Typography>
                      <Typography className={classes.statLabel} component="div">
                        {stat.label}
                      </Typography>
                      <Typography className={classes.statNote} component="div">
                        {stat.note}
                      </Typography>
                    </Card>
                  </AnimatedSection>
                </Grid>
              ))}
            </Grid>
          </section>

          <section className={classes.tabsSection} id="service-categories">
            <AnimatedSection animation="fadeInUp" delay={0}>
              <Box className={classes.sectionHeading}>
                <Typography variant="overline" className={classes.sectionEyebrow}>
                  Service architecture
                </Typography>
                <Typography component="h2" variant="h3" className={classes.sectionTitle}>
                  Choose the creative lane that matches your goals.
                </Typography>
                <Typography className={classes.sectionDescription}>
                  Each category is arranged around a clear outcome so your team can move from scope to delivery without friction.
                </Typography>
              </Box>
            </AnimatedSection>

            <div className={classes.tabsShell}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="digital media service categories"
                className={classes.tabs}
                indicatorColor="primary"
                textColor="primary"
              >
                {digitalMediaServices.map((categoryData, index) => (
                  <Tab
                    key={categoryData.category}
                    label={(
                      <span className={classes.tabLabel}>
                        <span className={classes.tabIcon}>{categoryData.icon}</span>
                        <span>{categoryData.category}</span>
                      </span>
                    )}
                    id={`digital-media-tab-${index}`}
                    aria-controls={`digital-media-tabpanel-${index}`}
                    className={classes.tab}
                  />
                ))}
              </Tabs>
            </div>

            <div className={classes.tabContent}>
              {digitalMediaServices.map((categoryData, categoryIndex) => {
                return (
                  <TabPanel key={categoryData.category} value={tabValue} index={categoryIndex}>
                    <Grid container spacing={3}>
                      {categoryData.services.map((service, serviceIndex) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={service.title}>
                          <AnimatedSection animation="fadeInUp" delay={serviceIndex * 50}>
                            <Card className={classes.simpleServiceCard} elevation={0}>
                              <span className={classes.serviceCardAccent} aria-hidden="true" />
                              <Box className={classes.simpleServiceContent}>
                                <Box className={classes.simpleServiceHeader}>
                                  <Typography variant="h5" component="h3" className={classes.simpleServiceTitle}>
                                    {service.title}
                                  </Typography>
                                  {(service.estimatedTime || service.pricing) && (
                                    <Box className={classes.serviceMetaPills}>
                                      {service.estimatedTime && (
                                        <span className={classes.serviceMetaPill}>
                                          <span className={classes.serviceMetaPillDot} aria-hidden="true" />
                                          {service.estimatedTime}
                                        </span>
                                      )}
                                      {service.pricing && (
                                        <span className={`${classes.serviceMetaPill} ${classes.serviceMetaPillAccent}`}>
                                          {service.pricing}
                                        </span>
                                      )}
                                    </Box>
                                  )}
                                </Box>

                                <Typography className={classes.simpleServiceDescription}>
                                  {service.description}
                                </Typography>

                                {service.deliverables && service.deliverables.length > 0 && (
                                  <Box className={classes.simpleServiceFeatures}>
                                    <Typography variant="subtitle2" className={classes.featuresLabel}>
                                      What's included
                                    </Typography>
                                    <ul className={classes.featuresList}>
                                      {service.deliverables.slice(0, 4).map((item) => (
                                        <li key={item}>
                                          <Typography variant="body2">{item}</Typography>
                                        </li>
                                      ))}
                                    </ul>
                                  </Box>
                                )}

                                <div className={classes.serviceCardFooter}>
                                  <Button
                                    variant="text"
                                    disableRipple
                                    className={classes.learnMoreButton}
                                    onClick={() => handleOpenModal(service, categoryData.category)}
                                  >
                                    Learn more
                                    <span className={classes.learnMoreArrow} aria-hidden="true">→</span>
                                  </Button>
                                </div>
                              </Box>
                            </Card>
                          </AnimatedSection>
                        </Grid>
                      ))}
                    </Grid>
                  </TabPanel>
                );
              })}
            </div>
          </section>

          <section className={classes.processSection}>
            <AnimatedSection animation="fadeInUp" delay={0}>
              <Box className={classes.sectionHeading}>
                <Typography variant="overline" className={classes.sectionEyebrow}>
                  Delivery model
                </Typography>
                <Typography component="h2" variant="h3" className={classes.sectionTitle}>
                  A structured workflow keeps the work moving.
                </Typography>
                <Typography className={classes.sectionDescription}>
                  A clear process keeps the page premium, the collaboration efficient, and the results easier to trust.
                </Typography>
              </Box>

              <div className={classes.processFlow}>
                {processSteps.map((step, index) => (
                  <React.Fragment key={step.step}>
                    <div className={classes.processNode}>
                      <span className={classes.processStep}>{step.step}</span>
                      <Card className={classes.processCard} elevation={0}>
                        <Typography className={classes.processIndex}>
                          Step {index + 1}
                        </Typography>
                        <Typography variant="h6" component="h3" className={classes.processTitle}>
                          {step.title}
                        </Typography>
                        <Typography className={classes.processText}>
                          {step.text}
                        </Typography>
                      </Card>
                    </div>
                    {index < processSteps.length - 1 && (
                      <div className={classes.processConnector} aria-hidden="true">
                        <span className={classes.processConnectorLine} />
                        <span className={classes.processConnectorArrow}>›</span>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </AnimatedSection>
          </section>

          <section ref={contactRef} className={classes.contactSection} style={{ scrollMarginTop: '120px' }}>
            <AnimatedSection animation="fadeInUp" delay={0}>
              <Box className={classes.sectionHeading} align="center">
                <Typography variant="overline" className={classes.sectionEyebrow}>
                  Ready to move
                </Typography>
                <Typography component="h2" variant="h3" className={classes.sectionTitle}>
                  Let’s shape a digital media plan that feels strategic and premium.
                </Typography>
                <Typography className={classes.sectionDescription}>
                  Share the scope, and we’ll map the right mix of content, production, and campaign support.
                </Typography>
              </Box>
            </AnimatedSection>

            <Grid container spacing={4} alignItems="stretch">
              <Grid item xs={12} md={4}>
                <AnimatedSection animation="fadeInUp" delay={80}>
                  <Card className={classes.contactIntroCard} elevation={0}>
                    <Chip label="Fast response" className={classes.contactChip} />
                    <Typography variant="h4" component="h3" className={classes.contactTitle}>
                      Start with one service or build a full campaign stack.
                    </Typography>
                    <Typography className={classes.contactText}>
                      We’ll help you define scope, assets, and the right delivery path. The form pre-fills the service or category you select.
                    </Typography>
                    <div className={classes.contactPills}>
                      {['Strategy', 'Production', 'Optimization'].map((item) => (
                        <Chip key={item} label={item} className={classes.contactMiniChip} />
                      ))}
                    </div>
                  </Card>
                </AnimatedSection>
              </Grid>

              <Grid item xs={12} md={8}>
                <AnimatedSection animation="fadeInUp" delay={140}>
                  <Card className={classes.contactFormCard} elevation={0}>
                    <ContactForm prefilledService={prefilledService} />
                  </Card>
                </AnimatedSection>
              </Grid>
            </Grid>
          </section>
        </Container>
      </div>

      <Modal
        open={open}
        onClose={handleCloseModal}
        aria-labelledby="service-modal"
        className={classes.modal}
      >
        <Box className={classes.modalContent}>
          {selectedService && (
            <>
              <Box className={classes.modalHeader}>
                <div>
                  <Typography variant="h5" component="h3" className={classes.modalTitle}>
                    {selectedService.title}
                  </Typography>
                </div>
                <Button onClick={handleCloseModal} className={classes.closeButton} variant="text">
                  ✕
                </Button>
              </Box>

              <Box className={classes.modalBody}>
                <Typography variant="body1" className={classes.modalDescription}>
                  {selectedService.description}
                </Typography>

                {selectedService.deliverables && selectedService.deliverables.length > 0 && (
                  <Box className={classes.deliverablesSection}>
                    <Typography variant="h6" className={classes.deliverableTitle}>
                      What's included
                    </Typography>
                    <ul className={classes.deliverablesList}>
                      {selectedService.deliverables.map((item) => (
                        <li key={item}>
                          <Typography variant="body2">{item}</Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}

                {selectedService.tools && selectedService.tools.length > 0 && (
                  <Box className={classes.toolsSection}>
                    <Typography variant="h6" className={classes.toolsTitle}>
                      Tools & technologies
                    </Typography>
                    <Box className={classes.toolsGrid}>
                      {selectedService.tools.map((tool) => (
                        <Chip key={tool} label={tool} className={classes.toolChipLarge} />
                      ))}
                    </Box>
                  </Box>
                )}

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  className={classes.requestQuoteButton}
                  onClick={() => handleRequestQuote(selectedService.title)}
                >
                  Request a Quote
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
}
